import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Spade from "@/components/Spade";
import { useLight } from "@/context/LightContext";
import {
    ArrowUpRight,
    ArrowLeft,
    Instagram,
    Dumbbell,
    Stethoscope,
    UtensilsCrossed,
    Scissors,
    ShoppingBag,
    Wrench,
    Send,
} from "lucide-react";

const WA_URL =
    "https://wa.me/919717622852?text=" +
    encodeURIComponent(
        "Hi, I run a local business and I want to book my $79 Aestherium trial week.",
    );
const IG_URL = "https://instagram.com/aestherium.media";

const PROBLEMS = [
    {
        title: "You pay a commission on customers who live around the corner.",
        body: "Aggregator platforms take a cut of every order, every booking, every enquiry. These are people in your own neighbourhood. You should not have to pay a middleman to reach someone who lives five minutes away.",
    },
    {
        title: "Discounts bring people in once. Then they leave.",
        body: "Every offer you run trains customers to wait for the next offer. You get footfall without loyalty and volume without margin. That is not growth, it is a treadmill.",
    },
    {
        title: "The place down the street isn't better than you. It's just louder.",
        body: "Same service, similar pricing, and they are packed while you are not. The difference is not quality. It is who the neighbourhood thinks of first.",
    },
];

const CASH = [
    {
        letter: "C",
        title: "Content",
        body: "Your space, your team, your work, your results. We build content that shows people what walking in actually feels like, so the decision is already made before they arrive.",
    },
    {
        letter: "A",
        title: "Amplify",
        body: "Organic distribution targeted to your area. Reach that lands with people who can actually visit you, not followers in another country who will never walk through your door.",
    },
    {
        letter: "S",
        title: "Sell",
        body: "Views become bookings, enquiries, and walk-ins. Profile, bio, highlights, and DMs rebuilt into a path that ends at your door instead of trailing off.",
    },
    {
        letter: "H",
        title: "Hook",
        body: "Most people are not looking for you today. Retention systems keep you visible until the week they are, so when the need appears, you are the first name in their head.",
    },
];

const AUDIENCES = [
    { icon: Dumbbell, title: "Gyms & Fitness Studios", body: "Fill memberships without discounting yourself into a corner." },
    { icon: Stethoscope, title: "Clinics & Doctors", body: "Build the trust that makes patients choose you before they call." },
    { icon: UtensilsCrossed, title: "Restaurants & Cafes", body: "Tables filled by people who came for you, not for a coupon." },
    { icon: Scissors, title: "Salons & Spas", body: "Turn your work into the reason people book, not your price list." },
    { icon: ShoppingBag, title: "Retail & Boutiques", body: "Footfall from your own neighbourhood, without platform fees." },
    { icon: Wrench, title: "Service Businesses", body: "Plumbers, tutors, studios, repair shops. If people come to you, this works." },
];

const STEPS = [
    { n: "01", title: "Free Audit", body: "We look at your profile, your area, and your current content, and show you exactly where your visibility is leaking." },
    { n: "02", title: "Trial Week. $79.", body: "We run the full system on your account for seven days. You watch the numbers move in real time." },
    { n: "03", title: "Weekly Partnership", body: "If it works, we continue. Simple weekly billing, no long contracts, no lock-in." },
    { n: "04", title: "Neighbourhood Ownership", body: "We compound it. More reach, more enquiries, until you are the name people say without thinking." },
];

const FAQS = [
    { q: "Do I need to be on camera?", a: "It helps but it is not required. Your space, your food, your equipment, your work, and your team all carry content. We build around whatever you are comfortable doing." },
    { q: "My business is small and local. Is Instagram even worth it?", a: "Local is exactly where this works best. You do not need a million views. You need the few thousand people near you to know you exist and think of you first. That is a far easier target than going viral, and far more profitable." },
    { q: "How is this different from paying for listings on delivery or booking platforms?", a: "Those platforms rent you a customer and keep a cut forever. Organic reach is yours. When someone messages you from your own content, there is no commission and no competitor listed underneath you." },
    { q: "Do you post for me, or do I?", a: "We handle it. Strategy, content direction, captions, hooks, posting, engagement, reporting. You keep running your business." },
    { q: "I already run offers and discounts. Should I stop?", a: "Not immediately, but the goal is to make them unnecessary. Discounts buy a visit. Visibility and trust buy a regular. We build the second so you can stop relying on the first." },
    { q: "What happens in the trial week?", a: "The real work, not a demo. Audit, strategy, profile rebuild, content plan, and the distribution system running live on your account. You see actual movement in seven days." },
];

const CHIPS = ["190+ Brands Scaled", "200M+ Reach in 7 Days", "Zero Ad Spend"];

export default function LocalBusiness() {
    const { activate, setTheme } = useLight();

    useEffect(() => {
        setTheme("navy");
        const cx = typeof window !== "undefined" ? window.innerWidth / 2 : 0;
        const cy = typeof window !== "undefined" ? window.innerHeight / 3 : 0;
        activate(cx, cy);
        window.scrollTo(0, 0);
        return () => setTheme("gold");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const prevTitle = document.title;
        document.title = "Instagram Marketing for Local Business — Aestherium Media";
        const desc =
            "Organic Instagram growth for gyms, clinics, restaurants, salons and neighbourhood businesses. Stop paying rent on your own postcode. $79 trial week, zero ad spend.";
        const keywords =
            "instagram marketing for local business, social media for gyms and restaurants, organic instagram growth for small business, local business marketing, aestherium media";
        const setMeta = (name, content, attr = "name") => {
            let m = document.querySelector(`meta[${attr}="${name}"]`);
            if (!m) {
                m = document.createElement("meta");
                m.setAttribute(attr, name);
                document.head.appendChild(m);
            }
            m.setAttribute("content", content);
            return m;
        };
        const created = [
            setMeta("description", desc),
            setMeta("keywords", keywords),
            setMeta("og:title", "Aestherium — Local Business Instagram Growth", "property"),
            setMeta("og:description", desc, "property"),
            setMeta("og:type", "website", "property"),
        ];
        return () => {
            document.title = prevTitle;
            created.forEach((m) => m?.remove());
        };
    }, []);

    return (
        <div className="min-h-screen text-[var(--text-primary)] relative" data-testid="local-business-page">
            <div className="absolute top-6 left-6 z-40">
                <Link
                    to="/"
                    data-testid="local-business-back"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border-subtle)] text-[var(--accent-champagne)]/80 hover:text-[var(--accent-champagne)] hover:border-[var(--border-accent)] backdrop-blur-md bg-black/25 text-xs uppercase tracking-[0.2em]"
                >
                    <ArrowLeft size={13} /> Back through the door
                </Link>
            </div>

            {/* HERO */}
            <section className="relative min-h-[100svh] flex items-center overflow-hidden pt-24 md:pt-32 pb-16">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute inset-0 bg-gradient-to-b from-[#1B3A6B] via-[#12294F] to-transparent" />
                    <div className="absolute inset-0 opacity-50 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.15),transparent_60%)]" />
                    <div className="absolute right-[-6%] top-[8%] opacity-[0.08] rotate-[8deg]">
                        <Spade size={520} />
                    </div>
                    <div className="absolute left-[-4%] bottom-[-10%] opacity-[0.05] -rotate-[12deg]">
                        <Spade size={380} />
                    </div>
                </div>
                <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 relative">
                    <div data-lit-target>
                        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-xs md:text-sm uppercase tracking-[0.35em] text-[var(--accent-champagne)]/80 mb-6">
                            For Gyms, Clinics, Restaurants, Salons & Local Brands
                        </motion.p>
                        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15 }} className="font-display font-bold leading-[1.05] tracking-tight text-[var(--text-primary)] wordmark-emboss" style={{ fontSize: "clamp(2rem, 6.5vw, 5rem)" }}>
                            Your Competitor Isn&apos;t Better.{" "}
                            <span className="italic text-[var(--accent-champagne)]">They&apos;re Just More Visible.</span>
                        </motion.h1>
                        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35 }} className="mt-6 md:mt-8 text-lg md:text-xl text-[var(--text-secondary)] max-w-3xl leading-relaxed">
                            Aestherium makes local businesses the obvious choice in their area. The people who live near you find you, trust you, and walk in. Organically.{" "}
                            <span className="text-[var(--accent-champagne)]">No paid ads.</span> No platform commissions. No discount wars.
                        </motion.p>
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5 }} className="mt-8 flex flex-wrap gap-3">
                            {CHIPS.map((chip) => (
                                <span key={chip} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--border-accent)] bg-[rgba(212,175,55,0.06)] text-[var(--accent-champagne)] text-xs md:text-sm uppercase tracking-[0.18em]">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-gold)] soft-pulse" />
                                    {chip}
                                </span>
                            ))}
                        </motion.div>
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7 }} className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <a href={WA_URL} target="_blank" rel="noreferrer" data-testid="lb-hero-cta-trial" className="group inline-flex items-center gap-3 pl-6 pr-4 py-4 bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm rounded-full hover:bg-[var(--accent-champagne)] transition-all shadow-[0_20px_40px_-15px_rgba(212,175,55,0.6)] hover:translate-y-[-2px]">
                                Book Your $79 Trial Week
                                <span className="w-9 h-9 rounded-full bg-[var(--brand-dark)] flex items-center justify-center group-hover:rotate-45 transition-transform">
                                    <ArrowUpRight size={16} className="text-[var(--accent-gold)]" />
                                </span>
                            </a>
                            <a href={IG_URL} target="_blank" rel="noreferrer" data-testid="lb-hero-cta-ig" className="inline-flex items-center gap-2 px-6 py-4 border border-[var(--border-accent)] text-[var(--text-primary)] rounded-full text-sm uppercase tracking-[0.2em] hover:bg-white/5 transition-all">
                                <Instagram size={16} /> DM us on Instagram
                            </a>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* PROBLEM */}
            <Section eyebrow="The Honest Part" heading="You're renting your own customers.">
                <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                    {PROBLEMS.map((p, i) => (
                        <Card key={i} index={i}>
                            <h3 className="font-display text-xl md:text-2xl text-[var(--text-primary)] leading-tight mb-4">{p.title}</h3>
                            <p className="text-[var(--text-secondary)] leading-relaxed text-[15px]">{p.body}</p>
                        </Card>
                    ))}
                </div>
            </Section>

            {/* SHIFT */}
            <Section eyebrow="What Changes" heading="Become the default choice in your postcode.">
                <motion.p initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9 }} className="text-[var(--text-secondary)] text-lg md:text-xl leading-relaxed max-w-4xl">
                    Every area has one gym everyone joins, one clinic everyone recommends, one place everyone takes visitors to. That position is not built on discounts or ad spend. It is built on being visible, consistently, to the few thousand people who actually live near enough to walk in. That is a much smaller job than going viral,{" "}
                    <span className="text-[var(--accent-champagne)]">and it is the job we do for you.</span>
                </motion.p>
            </Section>

            {/* CASH */}
            <Section eyebrow="The Method" heading="The C.A.S.H Strategy, built for local.">
                <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                    {CASH.map((c, i) => (
                        <Card key={c.letter} index={i}>
                            <div className="flex items-baseline gap-4 mb-4">
                                <span className="font-display font-bold text-6xl md:text-7xl gold-shimmer leading-none">{c.letter}</span>
                                <span className="text-xs uppercase tracking-[0.3em] text-[var(--accent-champagne)]/80">{c.title}</span>
                            </div>
                            <p className="text-[var(--text-secondary)] leading-relaxed">{c.body}</p>
                        </Card>
                    ))}
                </div>
            </Section>

            {/* AUDIENCES */}
            <Section heading="Built for businesses with a door.">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {AUDIENCES.map((a, i) => {
                        const Icon = a.icon;
                        return (
                            <Card key={a.title} index={i} compact>
                                <div className="w-11 h-11 rounded-full border border-[var(--border-accent)] flex items-center justify-center mb-4 text-[var(--accent-gold)]">
                                    <Icon size={18} />
                                </div>
                                <h3 className="font-display text-lg md:text-xl text-[var(--text-primary)] mb-2">{a.title}</h3>
                                <p className="text-[var(--text-secondary)] text-sm leading-relaxed">{a.body}</p>
                            </Card>
                        );
                    })}
                </div>
            </Section>

            {/* MATH */}
            <Section eyebrow="The Math" heading="A handful of regulars pays for this.">
                <div className="max-w-4xl">
                    <p className="text-[var(--text-secondary)] text-lg leading-relaxed mb-6">
                        Work out what one regular customer is worth to you over a year. A gym membership, a patient who keeps coming back, a family that eats at your place twice a month. Now compare that to what you hand over in platform commissions and discount margin every single month. We are not asking you to spend more.{" "}
                        <span className="text-[var(--accent-champagne)]">We are asking you to stop paying rent on your own neighbourhood.</span>
                    </p>
                    <p className="text-sm text-[var(--text-secondary)]/80 italic border-l-2 border-[var(--accent-gold)]/60 pl-5 leading-relaxed">
                        We don&apos;t guarantee footfall, and any agency that does is guessing. What we guarantee is the work: real strategy, real distribution, real reporting, every single week.
                    </p>
                </div>
            </Section>

            {/* PROOF */}
            <div data-testid="lb-proof">
                <Section eyebrow="Proof, Not Promises" heading="Local business case studies land here first.">
                    <p className="text-[var(--text-secondary)] text-lg leading-relaxed max-w-3xl mb-4">
                        We&apos;re documenting the numbers with our first cohort of local business clients right now. As each week&apos;s reach, enquiries and walk-ins compound, the receipts will be posted here — raw and unedited, the same way we do it on the main site.
                    </p>
                    <p className="text-sm text-[var(--accent-champagne)]/80 max-w-3xl italic">
                        Want to be one of the case studies people quote in six months? Take the trial week.
                    </p>
                </Section>
                <Section>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((n) => (
                            <div key={n} data-testid={`lb-case-placeholder-${n}`} className="surface-3d rounded-2xl border border-dashed border-[var(--border-subtle)] p-8 min-h-[220px] flex flex-col justify-center items-center text-center opacity-70">
                                <span className="text-[10px] uppercase tracking-[0.35em] text-[var(--accent-champagne)]/60 mb-3">Case Study #{n}</span>
                                <p className="text-[var(--text-secondary)] text-sm">Local business client result — documenting now.</p>
                            </div>
                        ))}
                    </div>
                </Section>
            </div>

            {/* HOW IT WORKS */}
            <Section heading="From first message to first walk-in.">
                <div className="relative">
                    <div className="absolute left-4 md:left-6 top-2 bottom-2 w-px bg-gradient-to-b from-[var(--accent-gold)]/60 via-[var(--border-subtle)] to-transparent" />
                    <div className="space-y-8">
                        {STEPS.map((s, i) => (
                            <motion.div key={s.n} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, delay: i * 0.06 }} className="relative pl-14 md:pl-20">
                                <span className="absolute left-0 top-0 w-8 h-8 md:w-12 md:h-12 rounded-full bg-[var(--brand-dark)] border border-[var(--border-accent)] flex items-center justify-center text-[var(--accent-gold)] font-display text-xs md:text-sm">{s.n}</span>
                                <h3 className="font-display text-xl md:text-2xl text-[var(--text-primary)] mb-2">{s.title}</h3>
                                <p className="text-[var(--text-secondary)] leading-relaxed max-w-2xl">{s.body}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* FAQ */}
            <Section heading="Straight answers.">
                <div className="grid md:grid-cols-2 gap-4 lg:gap-6" data-testid="lb-faq">
                    {FAQS.map((f, i) => (<FaqItem key={i} q={f.q} a={f.a} />))}
                </div>
            </Section>

            <FinalCta />

            <footer className="border-t border-[var(--border-subtle)] py-10 text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                    <Spade size={20} />
                    <span className="font-display text-lg text-[var(--text-primary)]">
                        Aestherium<span className="text-[var(--accent-gold)]">.</span>
                    </span>
                </div>
                <p className="text-[var(--text-secondary)]/60 text-xs uppercase tracking-[0.3em]">
                    Organic Reach. Real Sales. Zero Ads.
                </p>
                <Link to="/" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[var(--accent-champagne)]/80 hover:text-[var(--accent-champagne)]">
                    <ArrowLeft size={12} /> Back through the door
                </Link>
            </footer>
        </div>
    );
}

function Section({ eyebrow, heading, children }) {
    return (
        <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {(eyebrow || heading) && (
                    <div className="mb-10 md:mb-16 max-w-4xl">
                        {eyebrow && (
                            <p className="font-fraktur text-[var(--accent-gold)] text-sm tracking-[0.2em] mb-4 opacity-80">{eyebrow}</p>
                        )}
                        {heading && (
                            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.85 }} className="font-display font-bold text-3xl md:text-5xl leading-tight text-[var(--text-primary)]">
                                {heading}
                            </motion.h2>
                        )}
                    </div>
                )}
                {children}
            </div>
        </section>
    );
}

function Card({ children, index = 0, compact = false }) {
    return (
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.85, delay: index * 0.06 }} data-glow-catch className={`tilt-card surface-3d rounded-2xl border border-[var(--border-subtle)] ${compact ? "p-6" : "p-7 md:p-8"} bg-[rgba(255,255,255,0.02)]`}>
            {children}
        </motion.div>
    );
}

function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <button type="button" onClick={() => setOpen((v) => !v)} className="text-left surface-3d rounded-2xl border border-[var(--border-subtle)] p-6 hover:border-[var(--border-accent)] transition-colors">
            <div className="flex items-start gap-4">
                <span className="font-display text-2xl text-[var(--accent-gold)] leading-none">{open ? "−" : "+"}</span>
                <div className="flex-1">
                    <h4 className="font-display text-lg text-[var(--text-primary)] mb-2">{q}</h4>
                    {open && <p className="text-[var(--text-secondary)] leading-relaxed text-[15px] mt-2">{a}</p>}
                </div>
            </div>
        </button>
    );
}

function FinalCta() {
    const [form, setForm] = useState({ name: "", business: "", ig_handle: "", email: "", message: "" });
    const [status, setStatus] = useState("idle");
    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            await api.post("/leads", { ...form, source: "local-business" });
            setStatus("sent");
            setForm({ name: "", business: "", ig_handle: "", email: "", message: "" });
        } catch {
            setStatus("error");
        }
    };
    return (
        <section className="py-20 md:py-28 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <div data-testid="lb-final-cta" className="grid lg:grid-cols-2 gap-10 lg:gap-16 surface-3d rounded-3xl border border-[var(--border-accent)] p-8 md:p-12 lg:p-16">
                    <div>
                        <p className="font-fraktur text-[var(--accent-gold)] text-sm tracking-[0.2em] mb-4 opacity-80">Your Neighbourhood. Your Move.</p>
                        <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">Own Your Neighbourhood.</h2>
                        <p className="text-[var(--text-secondary)] text-lg leading-relaxed mb-6">
                            You already do the hard part every day. Give it the system that puts it in front of the people living close enough to walk in.{" "}
                            <span className="gold-shimmer font-semibold">$79</span>, one week, the real work.
                        </p>
                        <ul className="space-y-3">
                            {["No card details on this site", "Weekly billing after trial, no long contracts", "Audit and strategy within 24 hours"].map((b) => (
                                <li key={b} className="flex items-center gap-3 text-[var(--text-primary)]">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-gold)]" />
                                    {b}
                                </li>
                            ))}
                        </ul>
                        <a href={WA_URL} target="_blank" rel="noreferrer" data-testid="lb-final-wa" className="mt-8 inline-flex items-center gap-3 pl-6 pr-4 py-4 bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm rounded-full hover:bg-[var(--accent-champagne)] transition-all">
                            Book My $79 Trial Week
                            <span className="w-9 h-9 rounded-full bg-[var(--brand-dark)] flex items-center justify-center">
                                <ArrowUpRight size={16} className="text-[var(--accent-gold)]" />
                            </span>
                        </a>
                    </div>
                    <form onSubmit={submit} className="space-y-4" data-testid="lb-lead-form">
                        {[
                            ["name", "Your Name", "text", true],
                            ["business", "Business", "text", false],
                            ["ig_handle", "Instagram Handle", "text", false],
                            ["email", "Email", "email", true],
                        ].map(([k, label, type, required]) => (
                            <label key={k} className="block">
                                <span className="block text-[10px] uppercase tracking-[0.3em] text-[var(--accent-champagne)]/70 mb-2">{label}</span>
                                <input type={type} required={required} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:border-[var(--accent-gold)] focus:outline-none transition-colors" />
                            </label>
                        ))}
                        <label className="block">
                            <span className="block text-[10px] uppercase tracking-[0.3em] text-[var(--accent-champagne)]/70 mb-2">Notes (optional)</span>
                            <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:border-[var(--accent-gold)] focus:outline-none transition-colors" placeholder="Current goals, biggest bottleneck…" />
                        </label>
                        <button type="submit" disabled={status === "sending" || status === "sent"} data-testid="lb-lead-submit" className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm hover:bg-[var(--accent-champagne)] transition-colors disabled:opacity-70">
                            {status === "sending" ? "Sending…" : status === "sent" ? "Received. Talk soon." : "Book my $79 trial"}
                            <Send size={14} />
                        </button>
                        {status === "error" && <p className="text-sm text-red-300">Something went wrong — DM us on Instagram instead.</p>}
                    </form>
                </div>
            </div>
        </section>
    );
}
